import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  SeccionFormulario,
} from "../../../../components/base";
import { encabezadoTabla } from "../comunes";

// Cuántas acciones se ven en la fila antes de pasar el resto al menú de tres puntos.
const ACCIONES_VISIBLES = 2;

// Un breakpoint "desde" muestra la columna a partir de él; en pantallas menores se oculta.
const mostrarDesde = (desde) =>
  desde && desde !== "xs" ? { xs: "none", [desde]: "table-cell" } : undefined;

/**
 * Esqueleto común de las pestañas de historial del empleado (Vacaciones, Ausencia, Reposos,
 * Subsidios, Adelanto, Bonos y Aguinaldo): encabezado con la acción principal, y debajo la carga,
 * el error, el vacío o la tabla con sus acciones por fila. Cada pestaña conserva sus propias
 * reglas: aquí solo vive lo que se ve igual en todas.
 *
 * Acciones por fila: como máximo 2 visibles; el resto, y siempre las destructivas, en el menú ⋮.
 *
 * @param {object} props
 * @param {string} props.titulo Título de la sección (h2).
 * @param {React.ReactNode} [props.descripcion]
 * @param {string} props.etiquetaTabla Nombre de la tabla para lectores de pantalla.
 * @param {{ etiqueta: string, onClick: Function, icono?: React.ReactNode }} [props.agregar]
 *   Acción principal de la pestaña (contained). Si falta, no se dibuja.
 * @param {boolean} props.cargando
 * @param {boolean|string} props.error `true` o "conexion".
 * @param {Function} props.onReintentar
 * @param {string} [props.etiquetaCarga]
 * @param {Array} props.filas
 * @param {Array<{id: string, etiqueta: string, render: Function, alinear?: string, desde?: "xs"|"sm"|"md"|"lg", sx?: object}>} props.columnas
 * @param {Function} [props.detalleMovil] `(fila) => nodo` que se ve bajo la primera columna solo en el teléfono.
 * @param {Function} [props.acciones] `(fila) => [{ id, etiqueta, icono, onClick, peligro?, visible? }]`
 * @param {Function} props.etiquetaFila `(fila) => texto` para los aria-label de las acciones ("adelanto #3").
 * @param {{titulo: string, descripcion: string}} props.vacio
 * @param {React.ReactNode} [props.resumen] Se ve sobre la tabla (p. ej. el saldo de vacaciones).
 * @param {number} [props.filasPorPagina] Si se pasa, la tabla se pagina en el cliente.
 */
const HistorialTab = ({
  titulo,
  descripcion,
  etiquetaTabla,
  agregar,
  cargando,
  error,
  onReintentar,
  etiquetaCarga,
  filas,
  columnas,
  detalleMovil,
  acciones,
  etiquetaFila,
  vacio,
  resumen,
  filasPorPagina,
}) => {
  // En el teléfono no caben los botones de la fila junto a las cifras: todas pasan al menú de tres puntos.
  const pequena = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [menu, setMenu] = useState({ ancla: null, acciones: [] });
  const [pagina, setPagina] = useState(0);

  // Si la lista se acorta (p. ej. al borrar el último de la página) se vuelve a la primera.
  useEffect(() => {
    if (filasPorPagina && pagina > 0 && pagina * filasPorPagina >= filas.length) {
      setPagina(0);
    }
  }, [pagina, filas.length, filasPorPagina]);

  const botonAgregar = agregar ? (
    <Button variant="contained" startIcon={agregar.icono ?? <AddIcon />} onClick={agregar.onClick}>
      {agregar.etiqueta}
    </Button>
  ) : undefined;

  const accionesDe = (fila) =>
    acciones ? acciones(fila).filter((accion) => accion.visible !== false) : [];
  const hayAcciones = filas.some((fila) => accionesDe(fila).length > 0);

  const visibles = filasPorPagina
    ? filas.slice(pagina * filasPorPagina, (pagina + 1) * filasPorPagina)
    : filas;

  // El detalle del teléfono va bajo la primera columna que sí se ve en pantallas pequeñas.
  const indicePrincipal = columnas.findIndex((c) => !c.desde || c.desde === "xs");
  const vacia = !cargando && !error && filas.length === 0;

  let contenido;
  if (cargando) {
    contenido = (
      <CargandoTabla
        filas={4}
        columnas={Math.min(columnas.length, 5)}
        etiqueta={etiquetaCarga || "Cargando…"}
      />
    );
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : `No se pudo cargar: ${titulo.toLowerCase()}`
        }
        onReintentar={onReintentar}
      />
    );
  } else if (filas.length === 0) {
    contenido = (
      <EstadoVacio
        titulo={vacio.titulo}
        descripcion={vacio.descripcion}
        accion={botonAgregar}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table
            aria-label={etiquetaTabla}
            sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}
          >
            <TableHead>
              <TableRow>
                {columnas.map((columna) => (
                  <TableCell
                    key={columna.id}
                    sx={{
                      ...encabezadoTabla,
                      textAlign: columna.alinear,
                      ...(mostrarDesde(columna.desde)
                        ? { display: mostrarDesde(columna.desde) }
                        : {}),
                    }}
                  >
                    {columna.etiqueta}
                  </TableCell>
                ))}
                {hayAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.map((fila) => {
                const lista = accionesDe(fila);
                const destacadas = lista
                  .filter((accion) => !accion.peligro)
                  .slice(0, pequena ? 0 : ACCIONES_VISIBLES);
                const enMenu = lista.filter((accion) => !destacadas.includes(accion));
                return (
                  <TableRow key={fila.id} hover>
                    {columnas.map((columna, indice) => (
                      <TableCell
                        key={columna.id}
                        sx={{
                          textAlign: columna.alinear,
                          ...(mostrarDesde(columna.desde)
                            ? { display: mostrarDesde(columna.desde) }
                            : {}),
                          ...columna.sx,
                        }}
                      >
                        {columna.render(fila)}
                        {indice === indicePrincipal && detalleMovil ? (
                          <Box
                            sx={{
                              display: { xs: "block", sm: "none" },
                              color: "text.secondary",
                              typography: "caption",
                              mt: 0.5,
                            }}
                          >
                            {detalleMovil(fila)}
                          </Box>
                        ) : null}
                      </TableCell>
                    ))}
                    {hayAcciones ? (
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {destacadas.map((accion) => (
                          <Tooltip key={accion.id} title={accion.etiqueta}>
                            <IconButton
                              aria-label={`${accion.etiqueta}: ${etiquetaFila(fila)}`}
                              onClick={accion.onClick}
                              sx={{ color: "primary.main" }}
                            >
                              {accion.icono}
                            </IconButton>
                          </Tooltip>
                        ))}
                        {enMenu.length > 0 ? (
                          <Tooltip title="Más acciones">
                            <IconButton
                              aria-label={`Más acciones: ${etiquetaFila(fila)}`}
                              aria-haspopup="menu"
                              onClick={(e) => setMenu({ ancla: e.currentTarget, acciones: enMenu })}
                              sx={{ color: "text.secondary" }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {filasPorPagina && filas.length > filasPorPagina ? (
          <TablePagination
            component="div"
            count={filas.length}
            page={pagina}
            onPageChange={(e, nueva) => setPagina(nueva)}
            rowsPerPage={filasPorPagina}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            getItemAriaLabel={(tipo) =>
              ({
                first: "Primera página",
                last: "Última página",
                next: "Página siguiente",
                previous: "Página anterior",
              }[tipo])
            }
          />
        ) : null}
      </>
    );
  }

  return (
    <SeccionFormulario
      titulo={titulo}
      descripcion={descripcion}
      // Con la lista vacía la acción ya está en el estado vacío: no se repite.
      acciones={vacia ? undefined : botonAgregar}
      plana
    >
      <Box sx={{ mt: 2 }}>
        {resumen}
        {contenido}
      </Box>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        {menu.acciones.map((accion) => (
          <MenuItem
            key={accion.id}
            onClick={() => {
              setMenu((actual) => ({ ...actual, ancla: null }));
              accion.onClick();
            }}
            sx={{ color: accion.peligro ? "error.main" : undefined }}
          >
            <ListItemIcon sx={{ color: accion.peligro ? "error.main" : "inherit" }}>
              {accion.icono}
            </ListItemIcon>
            <ListItemText>{accion.etiqueta}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </SeccionFormulario>
  );
};

export default HistorialTab;
